/**
 * Input numérico con formato de separadores de miles/decimales según locale.
 *
 * - Muestra el valor formateado (ej: '1.234.567,89' en es-CL) cuando no está enfocado.
 * - Al enfocar: quita los separadores de miles para facilitar la edición.
 * - Al salir del foco (blur): vuelve a formatear.
 * - onChange devuelve un string numérico puro ('1234567.89') compatible con
 *   react-hook-form (register + Controller) y con handlers de estado directo.
 *
 * Uso con react-hook-form Controller:
 *   <Controller name="unit_price" control={control}
 *     render={({ field }) => <NumericInput {...field} locale={locale} decimals={2} />} />
 *
 * Uso con estado directo:
 *   <NumericInput value={line.unit_price} onChange={v => updateLine(i, 'unit_price', v)}
 *     locale={locale} decimals={2} className="..." />
 */
import { useState, useEffect, forwardRef } from 'react'
import {
  formatNumber,
  getDecimalSeparator,
  getThousandsSeparator,
  parseLocalizedNumber,
} from '@/lib/locale'

export interface NumericInputProps {
  value?: string | number | null
  onChange?: (value: string) => void
  onBlur?: () => void
  name?: string
  /** Para que un <label htmlFor> pueda asociarse. Sin esto ningún campo
   *  numérico del producto tiene label accesible. */
  id?: string
  locale?: string
  decimals?: number      // cifras decimales a mostrar. Default: 2
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  className?: string
  min?: number
  max?: number
  /** Para que el error del campo lo ANUNCIE el lector de pantalla, no solo se
   *  vea en rojo. Igual que el `id` del PR #1211: el componente no los
   *  aceptaba, así que ninguno de sus 15 usos podía describir su error. */
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      value,
      onChange,
      onBlur,
      name,
      id,
      locale = 'es-CL',
      decimals = 2,
      placeholder,
      disabled,
      readOnly,
      className,
      min,
      max,
      'aria-invalid': ariaInvalid,
      'aria-describedby': ariaDescribedBy,
    },
    ref,
  ) => {
    const [display, setDisplay] = useState('')
    const [focused, setFocused] = useState(false)

    // Sincronizar display cuando cambia el valor externo (ej: selección de producto)
    useEffect(() => {
      if (focused) return
      if (value === null || value === undefined || value === '') {
        setDisplay('')
        return
      }
      const num = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(num)) { setDisplay(''); return }
      setDisplay(formatNumber(num, locale, decimals))
    }, [value, locale, decimals, focused])

    const decSep = getDecimalSeparator(locale)
    const thouSep = getThousandsSeparator(locale)

    const handleFocus = () => {
      setFocused(true)
      // Quitar separadores de miles para que el usuario edite sin obstáculos
      setDisplay(prev => prev.split(thouSep).join(''))
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      // Permitir solo: dígitos, separador decimal del locale, signo negativo al inicio,
      // y como máximo `decimals` dígitos después del separador.
      const escapedDec = decSep === '.' ? '\\.' : decSep
      const decPart = decimals > 0 ? `(${escapedDec}[0-9]{0,${decimals}})?` : ''
      const allowed = new RegExp(`^-?[0-9]*${decPart}$`)
      if (raw !== '' && !allowed.test(raw)) return
      setDisplay(raw)
      onChange?.(parseLocalizedNumber(raw, locale))
    }

    const handleBlur = () => {
      setFocused(false)
      const stored = parseLocalizedNumber(display, locale)
      if (stored !== '') {
        const num = parseFloat(stored)
        // Truncar a la precisión configurada para que el valor almacenado coincida
        // con lo que se muestra. Sin esto, un usuario que tipea "100.567" verá
        // "100,57" pero el estado guardaría "100.567" y el backend lo rechazaría
        // silenciosamente (DecimalField max_digits=15, decimal_places=2).
        const rounded = Number.isFinite(num) ? num.toFixed(decimals) : stored
        setDisplay(formatNumber(parseFloat(rounded), locale, decimals))
        onChange?.(rounded)
      } else {
        setDisplay('')
        onChange?.('')
      }
      onBlur?.()
    }

    return (
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        name={name}
        id={id}
        value={display}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={className}
        autoComplete="off"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
      />
    )
  },
)

NumericInput.displayName = 'NumericInput'
